<section
  class="bg-card flex h-full flex-col overflow-hidden rounded-2xl p-6 shadow"
  aria-labelledby="court-occupancy-title"
  aria-describedby="court-occupancy-description"
>
  <div class="flex items-start justify-between gap-4">
    <div>
      <h2
        id="court-occupancy-title"
        class="text-lg font-medium leading-6 tracking-tight"
      >
        Court occupancy
      </h2>
      <p
        id="court-occupancy-description"
        class="text-secondary mt-1 max-w-3xl text-sm leading-5"
      >
        Booked court time as a percentage of available hours over the previous
        {{ reportingDays }} completed days.
        <span class="sr-only">
          Based on current venue schedules and excluding maintenance.
        </span>
      </p>
    </div>
    <div class="flex shrink-0 items-center gap-2">
      @if (!isLoading && !errorMessage && overallOccupancyPercent !== null) {
        <span
          class="court-occupancy-average"
          [attr.aria-label]="
            'Overall occupancy ' + overallOccupancyPercent + ' percent'
          "
        >
          Avg {{ overallOccupancyPercent }}%
        </span>
      }
      <button
        mat-icon-button
        type="button"
        aria-label="Refresh court occupancy"
        [disabled]="isLoading"
        (click)="load()"
      >
        <mat-icon [svgIcon]="'heroicons_outline:arrow-path'"></mat-icon>
      </button>
    </div>
  </div>

  @if (isLoading) {
    <div
      class="flex min-h-72 items-center justify-center"
      role="status"
      aria-label="Loading court occupancy"
    >
      <mat-progress-spinner
        diameter="36"
        mode="indeterminate"
      ></mat-progress-spinner>
    </div>
  } @else if (errorMessage) {
    <div
      class="mt-6 flex min-h-64 flex-col items-center justify-center rounded-xl border border-red-200 bg-red-50 p-6 text-center"
      role="alert"
    >
      <mat-icon
        class="text-red-600"
        [svgIcon]="'heroicons_outline:exclamation-triangle'"
      ></mat-icon>
      <p class="mt-3 text-sm text-red-700">{{ errorMessage }}</p>
      <button mat-stroked-button type="button" class="mt-4" (click)="load()">
        Try again
      </button>
    </div>
  } @else if (rows.length === 0) {
    <div
      class="text-secondary mt-6 flex min-h-64 flex-col items-center justify-center rounded-xl border border-dashed p-6 text-center"
    >
      <mat-icon [svgIcon]="'heroicons_outline:chart-bar'"></mat-icon>
      <p class="mt-3 text-sm">
        Add an active court and venue schedule to see occupancy.
      </p>
    </div>
  } @else {
    <div
      class="court-occupancy-scroll mt-5"
      role="img"
      aria-label="Court occupancy by court"
    >
      <apx-chart
        class="block w-full"
        [style.height.px]="chartHeight"
        [chart]="chartOptions.chart"
        [colors]="chartOptions.colors"
        [dataLabels]="chartOptions.dataLabels"
        [grid]="chartOptions.grid"
        [plotOptions]="chartOptions.plotOptions"
        [series]="chartOptions.series"
        [states]="chartOptions.states"
        [tooltip]="chartOptions.tooltip"
        [xaxis]="chartOptions.xaxis"
        [yaxis]="chartOptions.yaxis"
      ></apx-chart>
    </div>

    <ul class="sr-only">
      @for (row of rows; track trackByCourtId($index, row)) {
        <li>
          {{ row.courtName }} at {{ row.venueName }}:
          @if (row.occupancyPercent === null) {
            no scheduled bookable hours.
          } @else {
            {{ row.occupancyPercent }} percent occupied,
            {{ formatHours(row.occupiedMinutes) }} booked hours of
            {{ formatHours(row.bookableMinutes) }} bookable hours.
          }
        </li>
      }
    </ul>
  }
</section>
